import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';

import {
  COVER_MODE,
  DEFAULT_PAGINATION,
  POI_TYPE,
  THEME_POI_TYPE,
  THEME_TYPE,
} from '../../commons/constants/constants';
import { PageRequest, PaginationType } from '../../types/CommonTypes';
import { SceneData } from '../../types/SceneData';
import { ThemeData } from '../../types/ThemeData';
import { TourConfigData, TourData } from '../../types/TourData';
import { storeName as entityStore } from '../entities/tour.entity';
import { RootModel } from '../models';
import { storeName as toursStore } from './tour.model';

export const MERGE_STEPS = {
  TOURS: 'TOURS',
  SCENES: 'SCENES',
  SCENARIOS: 'SCENARIOS',
  FUNCTION_BUTTONS: 'FUNCTION_BUTTONS',
  SCENE_GROUPS: 'SCENE_GROUPS',
  SETTINGS: 'SETTINGS',
};

export const storeName = 'mergeTours';

export interface initialValuesType {
  tourIds: (number | string)[];
  pagination: PaginationType | null;
  criteria: { [key: string]: any };
  visible: boolean;
  loading: boolean;
  step: string;
  availableSteps: string[];

  baseId: number;
  otherId: number;

  sceneIds: { [sceneCode: string]: number[] };
  scenarioIds: number[];

  categoriesTourId: number;
  floorplansTourId: number;
  backgroundSoundTourId: number;
  settingTourId: number;
  blendModeTourId: number;
  themeTourId: number;
  funcBtnTourIds: number[];
  sceneGroupTourIds: number[];

  mergeData: any;
}

const initialState: initialValuesType = {
  tourIds: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  visible: false,
  loading: false,
  step: MERGE_STEPS.TOURS,
  availableSteps: [MERGE_STEPS.TOURS, MERGE_STEPS.SCENES],

  baseId: null,
  otherId: null,

  sceneIds: {},
  scenarioIds: [],

  categoriesTourId: null,
  floorplansTourId: null,
  backgroundSoundTourId: null,
  settingTourId: null,
  blendModeTourId: null,
  themeTourId: null,
  funcBtnTourIds: [],
  sceneGroupTourIds: [],

  mergeData: null,
};

export const mergeTours: any = createModel<RootModel>()({
  name: storeName,
  state: { ...initialState },
  reducers: {
    setTourIds: produce((state, payload) => {
      state.tourIds = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setBaseId: produce((state, payload) => {
      state.baseId = payload;
    }),
    setOtherId: produce((state, payload) => {
      state.otherId = payload;
    }),
    setSceneIds: produce((state, payload) => {
      state.sceneIds = payload;
    }),
    setMergeData: produce((state, payload) => {
      state.mergeData = {
        ...state.mergeData,
        ...payload,
      };
    }),
    reset: produce((state, payload) => {
      state.tourIds = [];
      state.pagination = DEFAULT_PAGINATION;
      state.criteria = {};
      state.visible = false;
      state.loading = false;
      state.step = MERGE_STEPS.TOURS;
      state.availableSteps = [MERGE_STEPS.TOURS, MERGE_STEPS.SCENES];

      state.baseId = null;
      state.otherId = null;

      state.sceneIds = {};
      state.scenarioIds = [];

      state.categoriesTourId = null;
      state.floorplansTourId = null;
      state.backgroundSoundTourId = null;
      state.settingTourId = null;
      state.blendModeTourId = null;
      state.themeTourId = null;
      state.funcBtnTourIds = [];
      state.sceneGroupTourIds = [];

      state.mergeData = null;
    }),
    setStep: produce((state, payload) => {
      state.step = payload;
    }),
    setAvailableSteps: produce((state, payload) => {
      state.availableSteps = payload;
    }),
    setNextStep: produce((state, _) => {
      const idx = state.availableSteps.indexOf(state.step);
      state.step = state.availableSteps[idx + 1];
    }),
    setScenarioIds: produce((state, payload) => {
      state.scenarioIds.splice(0, state.scenarioIds.length);
      state.scenarioIds.push(...(payload || []));
    }),
    setCategoriesTourId: produce((state, payload) => {
      state.categoriesTourId = payload;
    }),
    setFloorplansTourId: produce((state, payload) => {
      state.floorplansTourId = payload;
    }),
    setBackgroundSoundTourId: produce((state, payload) => {
      state.backgroundSoundTourId = payload;
    }),
    setSettingTourId: produce((state, payload) => {
      state.settingTourId = payload;
    }),
    setBlendModeTourId: produce((state, payload) => {
      state.blendModeTourId = payload;
    }),
    setThemeTourId: produce((state, payload) => {
      state.themeTourId = payload;
    }),
    setFuncBtnTourIds: produce((state, payload) => {
      state.funcBtnTourIds = payload;
    }),
    setSceneGroupTourIds: produce((state, payload) => {
      state.sceneGroupTourIds = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof mergeTours>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setTourIds(data.content.map((tour) => tour.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load tours');
      } finally {
        this.setLoading(false);
      }
    },
    async selectMergingTours(payload, state) {
      this.setLoading(true);
      try {
        const { baseId, otherId } = state[storeName];
        const { ids } = state[entityStore];
        const simplifiedBaseTour = ids[baseId];
        const simplifiedOtherTour = ids[otherId];

        const promises = [];
        promises.push(dispatch[entityStore].visualize(simplifiedBaseTour.code));
        promises.push(dispatch[entityStore].visualize(simplifiedOtherTour.code));
        const tours = await Promise.all(promises);

        const baseTour: TourData = tours.find((t) => t.id === baseId);
        const otherTour: TourData = tours.find((t) => t.id === otherId);

        const userContactIds: number[] = [];
        if (baseTour.userContacts?.length) {
          baseTour.userContacts.forEach((uc) => {
            if (!userContactIds.includes(uc.id)) {
              userContactIds.push(uc.id);
            }
          });
        }

        if (otherTour.userContacts?.length) {
          otherTour.userContacts.forEach((uc) => {
            if (!userContactIds.includes(uc.id)) {
              userContactIds.push(uc.id);
            }
          });
        }

        const mergeData: Partial<TourData> = {
          ...payload,
          splashScreen: baseTour.splashScreen,
          splashScreenLogo: baseTour.splashScreenLogo,
          tourLogo: baseTour.tourLogo,
          tags: baseTour.tags,
          userContacts: userContactIds.map((ucId) => ({ id: ucId })),
        };
        this.setMergeData(mergeData);
        this.setBackgroundSoundTourId(baseId);
        this.setSettingTourId(baseId);
        this.setCategoriesTourId(baseId);
        this.setFloorplansTourId(baseId);
        this.setBlendModeTourId(baseId);
        this.setThemeTourId(baseId);

        const steps = [MERGE_STEPS.TOURS, MERGE_STEPS.SCENES];
        if (!!baseTour.tourScenarios?.length || !!otherTour.tourScenarios?.length) {
          steps.push(MERGE_STEPS.SCENARIOS);
        }

        if (!!baseTour.functionButtons?.length || !!otherTour.functionButtons?.length) {
          steps.push(MERGE_STEPS.FUNCTION_BUTTONS);
        }

        if (!!baseTour.sceneGroups?.length || !!otherTour.sceneGroups?.length) {
          steps.push(MERGE_STEPS.SCENE_GROUPS);
        }

        steps.push(MERGE_STEPS.SETTINGS);
        this.setAvailableSteps(steps);
        this.setStep(MERGE_STEPS.SCENES);
      } catch (e) {
        console.error(e);
        message.error('Failed to merge tours');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeScenes(payload, state) {
      this.setLoading(true);
      try {
        const { baseId, otherId, mergeData, sceneIds = {} } = state[storeName];
        const { ids } = state[entityStore];
        const baseTour = ids[baseId];
        const otherTour = ids[otherId];

        const baseSceneIds = baseTour.scenes.map((scn) => scn.id);
        const otherSceneIds = otherTour.scenes.map((scn) => scn.id);

        const scenes: SceneData[] = baseTour.scenes
          .filter((scn) => !otherSceneIds.includes(scn.id))
          .concat(otherTour.scenes.filter((scn) => !baseSceneIds.includes(scn.id)));

        if (sceneIds) {
          Object.entries(sceneIds as { [sceneCode: string]: number[] }).forEach(
            ([sceneCode, tourIds]) => {
              let baseScene;
              if (tourIds.includes(baseId)) {
                baseScene = baseTour.scenes.find((scn) => scn.code === sceneCode);
              }

              let otherScene;
              if (tourIds.includes(otherId)) {
                otherScene = otherTour.scenes.find((scn) => scn.code === sceneCode);
              }

              if (!baseScene && !otherScene) {
                baseScene = baseTour.scenes.find((scn) => scn.code === sceneCode);
              }

              const coverScene: SceneData = baseScene || otherScene;
              const { coverMode, vlookatmax, vlookatmin, hlookatmax, hlookatmin } = coverScene;
              const nadirPois = coverScene.pois.filter((p) => p.type === POI_TYPE.NADIR_LOGO);

              if (baseScene && otherScene) {
                const pois = [...baseScene.pois, ...otherScene.pois].filter(
                  (p) => p.type !== POI_TYPE.NADIR_LOGO
                );

                scenes.push({
                  ...baseScene,
                  category: {},
                  coverMode: coverScene.coverMode,
                  vlookatmax: coverMode === COVER_MODE.LOCK_VIEW ? vlookatmax : 90,
                  vlookatmin: coverMode === COVER_MODE.LOCK_VIEW ? vlookatmin : -90,
                  hlookatmax: coverMode === COVER_MODE.LOCK_VIEW ? hlookatmax : 360,
                  hlookatmin: coverMode === COVER_MODE.LOCK_VIEW ? hlookatmin : -360,
                  pois: coverMode === COVER_MODE.NADIR_LOGO ? pois.concat(nadirPois) : pois,
                });
              } else {
                const pois = coverScene.pois.filter((p) => p.type !== POI_TYPE.NADIR_LOGO);
                scenes.push({
                  ...(baseScene || otherScene),
                  category: {},
                  coverMode: coverScene.coverMode,
                  vlookatmax: coverMode === COVER_MODE.LOCK_VIEW ? vlookatmax : 90,
                  vlookatmin: coverMode === COVER_MODE.LOCK_VIEW ? vlookatmin : -90,
                  hlookatmax: coverMode === COVER_MODE.LOCK_VIEW ? hlookatmax : 360,
                  hlookatmin: coverMode === COVER_MODE.LOCK_VIEW ? hlookatmin : -360,
                  pois: coverMode === COVER_MODE.NADIR_LOGO ? pois.concat(nadirPois) : pois,
                });
              }
            }
          );
        }

        this.setMergeData({
          ...mergeData,
          scenes,
        });

        this.setNextStep();
      } catch (e) {
        console.error(e);
        message.error('Failed to merge scenes');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeScenarios(payload, state) {
      this.setLoading(true);
      try {
        const { baseId, otherId, mergeData, scenarioIds } = state[storeName];
        const { ids } = state[entityStore];
        const baseTour = ids[baseId];
        const otherTour = ids[otherId];

        const scenarios = (baseTour.tourScenarios || [])
          .concat(otherTour.tourScenarios || [])
          .filter((sce) => {
            return scenarioIds.includes(sce.id);
          });

        this.setMergeData({
          ...mergeData,
          tourScenarios: scenarios,
        });

        this.setNextStep();
      } catch (e) {
        console.error(e);
        message.error('Failed to merge scenarios');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeFunctionButtons(payload, state) {
      this.setLoading(true);
      try {
        const { mergeData, funcBtnTourIds = [] } = state[storeName];
        if (!funcBtnTourIds.length) {
          this.setNextStep();
          return;
        }

        this.setMergeData({
          ...mergeData,
          functionButtonTourIds: funcBtnTourIds,
        });

        this.setNextStep();
      } catch (e) {
        console.error(e);
        message.error('Failed to merge function buttons');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeSceneGroups(payload, state) {
      this.setLoading(true);
      try {
        const { mergeData, sceneGroupTourIds = [] } = state[storeName];
        if (!sceneGroupTourIds.length) {
          this.setNextStep();
          return;
        }

        this.setMergeData({
          ...mergeData,
          sceneGroupTourIds: sceneGroupTourIds,
        });

        this.setNextStep();
      } catch (e) {
        console.error(e);
        message.error('Failed to merge scene groups');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeSettings(payload, state) {
      this.setLoading(true);
      try {
        const {
          mergeData,
          categoriesTourId,
          floorplansTourId,
          backgroundSoundTourId,
          settingTourId,
          blendModeTourId,
          themeTourId,
        } = state[storeName];
        const { ids } = state[entityStore];

        let scenes: SceneData[] = mergeData.scenes as SceneData[];
        if (categoriesTourId) {
          const categoriesTour: TourData = ids[categoriesTourId];
          const sceneIdCateMap = categoriesTour.scenes.reduce((prev, curr: SceneData) => {
            if (curr.category) {
              prev[curr.id] = {
                ...curr.category,
                code: undefined,
                id: undefined,
                sourceId: curr.category.id,
                categoryGroup: curr.category?.categoryGroup
                  ? {
                      ...curr.category.categoryGroup,
                      code: undefined,
                      id: undefined,
                      sourceId: curr.category.categoryGroup.id,
                    }
                  : undefined,
              };
            }

            return prev;
          }, {});

          scenes = (mergeData.scenes as SceneData[]).map((scn) => {
            const category = sceneIdCateMap[scn.id];
            return {
              ...scn,
              category,
            };
          });
        }

        let floorplans = [];
        if (floorplansTourId) {
          const floorplanTour: TourData = ids[floorplansTourId];
          floorplans = (floorplanTour.floorplans || []).map((fp) => ({
            ...fp,
            code: undefined,
            id: undefined,
          }));
        }

        let bgSoundTour: TourData;
        if (backgroundSoundTourId) {
          bgSoundTour = ids[backgroundSoundTourId];
        }

        let theme: Partial<ThemeData> = null;
        if (themeTourId) {
          const themeTour: TourData = ids[themeTourId];
          if (themeTour.theme) {
            theme = {
              iconSetId: themeTour.theme.iconSetId,
              type: THEME_TYPE.TOUR,
              poiIcons: Object.keys(THEME_POI_TYPE).map((type) => {
                const poiIcon = (themeTour.theme.poiIcons || []).find((pi) => pi.poiType === type);
                return {
                  poiType: type,
                  icon: poiIcon?.icon,
                  themeId: null,
                };
              }),
            };
          }
        }

        const settingTour: TourConfigData = ids[settingTourId];
        const blendModeTour: TourData = ids[blendModeTourId];

        const newData = {
          ...mergeData,
          blendMode: blendModeTour.blendMode,
          blendSpeed: blendModeTour.blendSpeed,
          scenes,
          floorplans,
          theme,
          bgSound: bgSoundTour.bgSound,
          singleClick: settingTour.singleClick,
          markerColor: settingTour.markerColor,
          markerActiveColor: settingTour.markerActiveColor,
          allowScreenshot: settingTour.allowScreenshot,
          autoplayOnStart: settingTour.autoplayOnStart,
          startGuideWithAutoplay: settingTour.startGuideWithAutoplay,
          autoplayReplay: settingTour.autoplayReplay,
          showNavigationHotspots: settingTour.showNavigationHotspots,
          showNavigationHotspotsInAgt: settingTour.showNavigationHotspotsInAgt,
          showNavigationWithoutMovingHotspot: settingTour.showNavigationWithoutMovingHotspot,
          showNavigationHotspotLabels: settingTour.showNavigationHotspotLabels,
          enableLiveGuided: settingTour.enableLiveGuided,
          showWatermark: settingTour.showWatermark,
          keepView: settingTour.keepView,
          showTourName: settingTour.showTourName,
          bgVolumeDuringAGT: settingTour.bgVolumeDuringAGT,
          enableChat: settingTour.enableChat,
        };

        this.setMergeData(newData);
        await this.mergeTours(newData);
      } catch (e) {
        console.error(e);
        message.error('Failed to merge settings');
      } finally {
        this.setLoading(false);
      }
    },
    async mergeTours(payload, state) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].mergeTour(payload);
        this.reset();
        dispatch[toursStore].reloadList();
      } catch (e) {
        console.error(e);
        message.error('Failed to merge tours');
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default mergeTours;
